import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellIcon, CheckCheckIcon } from 'lucide-react';
import { cn } from '@bcecloud/portal-ui';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { isExternalHref, subjectHref, listNotifications, markAllNotificationsRead, markNotificationRead, relativeTime, type Notification } from '@/lib/notifications';

/** How often the bell asks for new rows. */
const POLL_MS = 60_000;
/** Rows the popover shows before "See all". */
const POPOVER_LIMIT = 8;

/**
 * The bell in the top bar: unread count, the latest rows, mark all read,
 * and the way to the full feed and preferences. Each row opens the event
 * tab that changed and marks itself read.
 */
export function NotificationBell(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const feed = useQuery({
    queryKey: ['notifications', 'bell'],
    queryFn: ({ signal }) => listNotifications({ limit: POPOVER_LIMIT }, signal),
    refetchInterval: POLL_MS,
    staleTime: 30_000,
  });
  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['notifications'] });
  }
  const readOne = useMutation({ mutationFn: (id: string) => markNotificationRead(id), onSettled: refresh });
  const readAll = useMutation({ mutationFn: () => markAllNotificationsRead(), onSettled: refresh });
  const unread = feed.data?.unread ?? 0;

  function open(notification: Notification): void {
    if (!notification.readAt) readOne.mutate(notification._id);
    // A client's thread lives on their portal: a new tab, with the staff sign-in.
    if (isExternalHref(notification.href)) {
      window.open(notification.href, '_blank', 'noopener');
      return;
    }
    navigate(notification.href ?? subjectHref(notification));
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={unread > 0 ? `${unread} unread notifications` : 'Notifications'}
          className="relative flex size-9 shrink-0 items-center justify-center rounded-portal-pill border border-portal-line text-portal-muted transition-colors hover:border-brand hover:text-brand"
        >
          <BellIcon aria-hidden className="size-4" />
          {unread > 0 ? (
            <span className="absolute -top-1 -right-1 grid min-w-4 place-items-center rounded-full bg-brand px-1 text-[10px] leading-4 font-bold text-white">{unread > 99 ? '99+' : unread}</span>
          ) : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[360px]">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {unread > 0 ? (
            <button type="button" onClick={() => readAll.mutate()} className="flex items-center gap-1 text-portal-micro font-semibold text-brand hover:underline">
              <CheckCheckIcon aria-hidden className="size-3.5" /> Mark all read
            </button>
          ) : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {(feed.data?.rows ?? []).length === 0 ? (
          <p className="px-2 py-4 text-center text-portal-small text-portal-muted">Nothing yet. Changes on your events land here.</p>
        ) : (
          (feed.data?.rows ?? []).map((notification) => (
            <DropdownMenuItem key={notification._id} onSelect={() => open(notification)} className="items-start gap-2">
              <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', notification.readAt ? 'bg-transparent' : 'bg-brand')} aria-hidden />
              <span className="min-w-0">
                <span className={cn('block truncate text-portal-small', notification.readAt ? 'text-portal-muted' : 'font-bold text-portal-ink')}>{notification.title}</span>
                {notification.body ? <span className="block truncate text-portal-micro text-portal-muted">{notification.body}</span> : null}
                <span className="block text-portal-micro text-portal-muted">{relativeTime(notification.createdAt)}</span>
              </span>
            </DropdownMenuItem>
          ))
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/notifications')}>See all and preferences</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
